import {
	shouldPolyfill as shouldPollyfillDatetime
} from '@formatjs/intl-datetimeformat/should-polyfill';
import {
	shouldPolyfill as shouldPollyfillDisplaynames
} from '@formatjs/intl-displaynames/should-polyfill';
import {
	shouldPolyfill as shouldPollyfillNumber
} from '@formatjs/intl-numberformat/should-polyfill';
import {
	shouldPolyfill as shouldPollyfillPluralRules
} from '@formatjs/intl-pluralrules/should-polyfill';
import {
	shouldPolyfill as shouldPollyfillRelativeTime
} from '@formatjs/intl-relativetimeformat/should-polyfill';
import moment from 'moment';
import { registerLocale, setDefaultLocale } from 'react-datepicker';

/**
 * Polyfills the global locale data given the chosen language for the application.
 *
 * @param {string} language  The language to use for the application.
 */
async function polyfillLocaleData(language) {

	// Polyfill locale data for moment-js

	try { moment.locale(language); } catch (error) { console.error(error); }

	// Polyfill locale data for @formatjs

	try {

		if (shouldPollyfillDatetime(language)) {

			// Load the polyfill 1st BEFORE loading data
			await import('@formatjs/intl-datetimeformat/polyfill-force');

			// Parallelize CLDR data loading
			await Promise.all([
				import('@formatjs/intl-datetimeformat/add-all-tz'),
				import(`@formatjs/intl-datetimeformat/locale-data/${language}`),
			]);

		}

		if (shouldPollyfillDisplaynames(language)) {

			// Load the polyfill 1st BEFORE loading data
			await import('@formatjs/intl-displaynames/polyfill-force');
			await import(`@formatjs/intl-displaynames/locale-data/${language}`);

		}

		if (shouldPollyfillNumber(language)) {

			// Load the polyfill 1st BEFORE loading data
			await import('@formatjs/intl-numberformat/polyfill-force');
			await import(`@formatjs/intl-numberformat/locale-data/${language}`);

		}

		if (shouldPollyfillPluralRules(language)) {

			// Load the polyfill 1st BEFORE loading data
			await import('@formatjs/intl-pluralrules/polyfill-force');
			await import(`@formatjs/intl-pluralrules/locale-data/${language}`);

		}

		if (shouldPollyfillRelativeTime(language)) {

			// Load the polyfill 1st BEFORE loading data
			await import('@formatjs/intl-relativetimeformat/polyfill-force');
			await import(`@formatjs/intl-relativetimeformat/locale-data/${language}`);

		}

	} catch (error) { console.error(error); }

	// Polyfill locale data for react-datepicker

	try {

		let finalLanguage = language;

		// NOTE: Swahili locale data is not supported as of now...
		if (finalLanguage === 'en' || finalLanguage === 'sw') {
			finalLanguage = 'en-US';
		}

		// date-fns v3 exposes locales as named exports off `date-fns/locale`
		// (`en-US` -> `enUS`); the per-locale `.js` paths of v2 are gone, and a
		// fully-dynamic bare specifier can't be bundled, so import the barrel
		// and pick the locale by its camel-cased key.
		const locales = await import('date-fns/locale');

		const localeKey = finalLanguage.replace(/-(\w)/g, (_match, char) => char.toUpperCase());

		const localeData = locales[localeKey] || locales.enUS;

		registerLocale(finalLanguage, localeData);

		setDefaultLocale(finalLanguage);

	} catch (error) { console.error(error); }

}

export {
	polyfillLocaleData,
};
